import type { Marathon } from '@/api/courses';

import { internalAppPath } from './storyLink';
import { isOpenCourse } from './studentFields';

/**
 * Гостевой режим приложения — правила без интерфейса (договор docs/CONTRACTS/guest-mode.md).
 *
 * Решение владельца 14.09.2026: приложение без входа открывается сразу на гостевой главной — слово
 * дня, карточки курсов, словарь. Всё личное («Начать» на курсе, «сохранить в словарь») ведёт на
 * регистрацию, а после неё — туда, куда гость нажимал.
 */

type CatalogCourse = Pick<Marathon, 'id' | 'is_free' | 'is_open'> & { is_guest?: boolean };

/**
 * Какие курсы показывать гостю.
 *
 * ⚠️ ПРИЗНАК «ДЛЯ ГОСТЯ» ВАЖНЕЕ «БЕСПЛАТНОГО» (D58). Сервер добавит поле `is_guest` позже. Если в
 * ответе каталога есть хоть один курс с этим признаком — показываем только такие, независимо от
 * `is_free`. Ни одного — бесплатные, как сейчас. Переключение само, без выката фронта.
 *
 * Поля пока нет вовсе — это обычный случай, а не ошибка: `undefined` значит «не для гостя».
 *
 * ⚠️ ЭТО НЕ ЗАЩИТА. Ручка каталога без входа отдаёт и платные курсы («IELTS Premium») — гость их
 * не видит на экране, но они в ответе. Закрывает фильтр на сервере (договор, раздел для B).
 */
export function guestCourses<T extends CatalogCourse>(items: readonly T[] | undefined): T[] {
  const list = items ?? [];
  const marked = list.filter((m) => m.is_guest === true);
  if (marked.length > 0) return marked;
  // `is_open`, раньше `is_free` (договор student-api.md §4).
  return list.filter(isOpenCourse);
}

/**
 * Куда вести после регистрации. Только внутренний путь кабинета `/app/…`, иначе `null` — и тогда
 * как обычно, на главную по роли. Чужой адрес из состояния перехода не принимаем.
 */
export function safeNext(next: unknown): string | null {
  return typeof next === 'string' ? internalAppPath(next) : null;
}
